<template>
	<OverlayBackdrop
		v-if="visible"
		@click="onOverlayClick"
		:portalOrder="portalOrder"
		:fullPage="fullPage"
		:loadedFilters="loadedFilters"
	>
		<component
			:is="htmlTag"
			:class="['modal', ...variantClassList]"
			v-if="visible"
		>
			<header class="modal__header">
				<div class="modal__content-container">
					<h2 class="modal__title">
						{{ title }}
					</h2>
					<p class="modal__description" v-if="$slots.header">
						<slot name="header">{{ description }}</slot>
					</p>
				</div>
				<span class="modal__close-btn" v-if="hasCloseBtn">
					<ButtonIcon
						@click="onClose"
						iconClassName="icon-remove-2"
						data-testid="close-btn"
					/>
				</span>
			</header>
			<div class="modal__body" data-testid="modalBody">
				<div class="modal__body-toolbar" v-if="hasBodyToolbar">
					<slot name="bodyToolbar" />
				</div>
				<slot name="body" />
			</div>
			<footer
				class="modal__footer"
				v-if="showApply || $slots.footer !== undefined"
			>
				<slot name="footer">
					<ButtonSet v-if="showApply" data-testid="set-btn">
						<ButtonClassic
							variant="primary"
							:labelText="applyLabelText"
							data-testid="save-btn"
							@click="onApply"
							:stopPropagation="true"
						/>
						<ButtonClassic
							@click="onCancel"
							variant="text"
							:labelText="closeLabelText"
							data-testid="btn-close"
							:stopPropagation="true"
						/>
					</ButtonSet>
				</slot>
			</footer>
		</component>
	</OverlayBackdrop>
</template>

<script>
import OverlayBackdrop from '@/components/visual/overlay-backdrop/overlay-backdrop.vue';
import ButtonClassic from '@/components/forms/button/button-classic.vue';
import ButtonIcon from '@/components/forms/button/button-icon.vue';
import BaseComponentMixin from '@/mixins/base-component-mixin.js';
import ButtonSet from '../../forms/button/button-set.vue';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import variantClasses from './variant-classes/modal-variant-classes';

/**
 * Modal
 * @param {('confirmation')} variant - the modal's visual variation
 */

export default {
	name: 'Modal',
	components: {
		ButtonIcon,
		ButtonClassic,
		ButtonSet,
		OverlayBackdrop
	},
	props: {
		visible: {
			type: Boolean,
			default: false
		},
		title: {
			type: String,
			default: ''
		},
		description: {
			type: String,
			default: ''
		},
		htmlTag: {
			type: String,
			default: 'div'
		},
		clickClose: {
			type: Function,
			default: () => {}
		},
		clickApply: {
			type: Function,
			default: () => {}
		},
		applyIconClassName: {
			type: String,
			default: ''
		},
		applyLabelText: {
			type: String,
			default: 'Apply'
		},
		closeIconClassName: {
			type: String,
			default: 'icon-close'
		},
		closeLabelText: {
			type: String,
			default: 'Close'
		},
		hasBodyToolbar: {
			type: Boolean,
			default: false
		},
		closeOnOverlayClick: {
			type: Boolean,
			default: true
		},
		hasCloseBtn: {
			type: Boolean,
			default: true
		},
		portalOrder: {
			type: Number,
			default: 1
		},
		fullPage: {
			type: Boolean,
			default: true
		},
		loadedFilters: {
			type: Boolean,
			default: true
		}
	},
	computed: {
		showApply() {
			return this.variant === 'confirmation';
		}
	},
	methods: {
		onCancel() {
			this.$emit('cancel');
			this.clickClose();
		},
		onClose() {
			this.$emit('close');
			this.clickClose();
		},
		onApply() {
			this.$emit('apply');
			this.clickApply();
		},
		onOverlayClick() {
			if (!this.showApply && this.closeOnOverlayClick) {
				this.onClose();
			}
		}
	},
	mixins: [BaseComponentMixin, componentVariantMixin],
	created() {
		this.variantClasses = variantClasses;
	}
};
</script>

<style lang="scss" scoped>
@include scrollbar('borderless');
/**
    * [Description]
    *
    * Global Modals
    * Can use SCSS variables = TRUE
    *
    * Both modal styles and variations are included here.
    *
    * [Table of Contents]
    *
	* BASE
	* CONFIRMATION
	* TABLET FULL WIDTH
 */

/*------------------------------------*\
  #BASE
\*------------------------------------*/
.modal {
	position: fixed;
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	top: 0;
	left: 0;
	max-width: 100%;
	width: 100%;
	min-width: 300px;
	height: 100vh;
	max-height: 100vh;
	border-radius: 0;
	background-color: var(--bg-0);
	color: var(--bg-0-fg-4);
	z-index: $z-level6;

	@include mq($from: md) {
		width: max-content;
		height: auto;
		max-height: 80vh;
		top: 30%;
		left: 50%;
		border-radius: $border-radius;
		transform: translate(-50%, -30%);
	}

	&__header {
		position: relative;
		display: flex;
		justify-content: space-between;
		padding: $modal-padding-y $modal-padding-x;
		border-radius: 0;
		line-height: normal;
		background: var(--bg-brand-1);
		color: var(--bg-brand-1-fg-2);

		@include mq($from: md) {
			border-top-left-radius: $border-radius;
			border-top-right-radius: $border-radius;
		}

		.btn-icon {
			&:not(:hover) {
				color: inherit;
			}

			&:hover,
			&:focus,
			&:active {
				color: var(--neutral-5);

				.icon:after {
					border-bottom: var(--neutral-5);
				}
			}
		}
	}

	&__content-container {
		display: flex;
		align-items: flex-start;
		flex-direction: column;
		align-self: center;
	}

	&__title {
		@include heading(md);
		& + *:not(.modal__description) {
			margin-top: $gutter-md;
		}
	}

	&__description {
		margin-top: $gutter-xs;
		font-size: $txt-font-size;
		& + * {
			margin-top: $gutter-md;
		}
	}

	&__close-btn {
		margin-left: $gutter-md;
	}

	&__body {
		padding: calc($modal-body-padding / 2);
		margin: calc($modal-body-padding / 2);
		overflow-y: auto;
		flex-grow: 2;
	}

	&__body-toolbar {
		position: sticky;
		top: 0;
		padding: $modal-body-padding;
		margin-bottom: $gutter-lg;
		margin-left: calc($modal-body-padding * -1);
		margin-right: calc($modal-body-padding / 2 * -1);

		background: var(--bg-0);
		z-index: $z-level1-1;

		&::before {
			@include scroll-fade(var(--bg-0), bottom);
		}

		&::after {
			content: '';
			position: absolute;
			left: 0;
			top: -($modal-body-padding + 1px);
			width: 100%;
			height: $modal-body-padding + 1px;
			background: var(--bg-0);
		}
	}

	&__footer {
		position: relative;
		padding: $modal-body-padding;
		text-align: right;

		&::before {
			@include scroll-fade(var(--bg-0));
		}
	}
}

/*------------------------------------*\
	#CONFIRMATION
\*------------------------------------*/
.modal--confirmation {
	width: calc(100vw - 32px);
	height: auto;
	max-height: 80vh;
	top: 30%;
	left: 50%;
	transform: translate(-50%, -30%);

	@include mq($from: 432px) {
		width: 450px;
		min-width: 400px;
	}
}

/*------------------------------------*\
	#TABLET FULL WIDTH
\*------------------------------------*/
.modal--tablet-full-width {
	@include mq($from: md) {
		width: 100%;
	}

	@include mq($from: lg) {
		width: max-content;
	}
}

/*------------------------------------*\
	#FOOTER DIVIDER
\*------------------------------------*/
.modal--footer-divider {
	> footer {
		&:before {
			border-bottom: $table-border-width solid var(--neutral-2);
			margin-bottom: $gutter-md;
		}
	}
}

/* LL styles v1 Only */
.modal {
	&__footer {
		margin-top: $gutter-md;
	}
}
</style>
